import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isSameOrBefore)

import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BlockIcon from '@mui/icons-material/Block'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

import { useCreateUnavailabilityMutation, usePreviewUnavailabilityMutation } from '@/core/services/meeting-link'
import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'

type CreateUnavailabilityDialogProps = {
  meetingLinkId: string
  availabilityId: string
}

export default function CreateUnavailabilityDialog({ meetingLinkId, availabilityId }: CreateUnavailabilityDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs())
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs())
  const [useTimeRange, setUseTimeRange] = useState(false)
  const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(dayjs().startOf('day').add(8, 'hours'))
  const [endTime, setEndTime] = useState<dayjs.Dayjs | null>(dayjs().startOf('day').add(12, 'hours'))
  const [reason, setReason] = useState('')
  const [affectedOrders, setAffectedOrders] = useState<MeetingLinkOrder[]>([])
  const [previewed, setPreviewed] = useState(false)

  const [createUnavailability, { isLoading: isCreating }] = useCreateUnavailabilityMutation()
  const [previewUnavailability, { isLoading: isPreviewing }] = usePreviewUnavailabilityMutation()

  const handleOpen = () => setOpen(true)

  const handleClose = () => {
    setOpen(false)
    resetForm()
  }

  const resetForm = () => {
    setStartDate(dayjs())
    setEndDate(dayjs())
    setUseTimeRange(false)
    setStartTime(dayjs().startOf('day').add(8, 'hours'))
    setEndTime(dayjs().startOf('day').add(12, 'hours'))
    setReason('')
    setAffectedOrders([])
    setPreviewed(false)
  }

  const getBody = () => ({
    startDate: startDate?.format('YYYY-MM-DD') ?? '',
    endDate: endDate?.format('YYYY-MM-DD') ?? '',
    ...(useTimeRange && startTime && endTime
      ? {
          startTime: startTime.hour() * 60 + startTime.minute(),
          endTime: endTime.hour() * 60 + endTime.minute(),
        }
      : {}),
  })

  const handlePreview = async () => {
    try {
      const result = await previewUnavailability({
        meetingLinkId,
        availabilityId,
        body: getBody(),
      }).unwrap()
      setAffectedOrders(result.affectedOrders)
      setPreviewed(true)
    } catch {
      // error handled by RTK Query
    }
  }

  const handleSubmit = async () => {
    try {
      await createUnavailability({
        meetingLinkId,
        availabilityId,
        body: {
          ...getBody(),
          reason: reason || undefined,
          cancelAffectedOrders: affectedOrders.length > 0,
        },
      }).unwrap()
      handleClose()
    } catch {
      // error handled by RTK Query
    }
  }

  const isValid = startDate && endDate && startDate.isSameOrBefore(endDate, 'day')

  return (
    <>
      <Tooltip title={t('page.meetingLink.unavailability.blockTime')}>
        <IconButton size="small" color="warning" onClick={handleOpen}>
          <BlockIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DialogTitle>{t('page.meetingLink.unavailability.create.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label={t('page.meetingLink.unavailability.create.startDate')}
                value={startDate}
                onChange={(value) => setStartDate(value as dayjs.Dayjs | null)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
              <DatePicker
                label={t('page.meetingLink.unavailability.create.endDate')}
                value={endDate}
                onChange={(value) => setEndDate(value as dayjs.Dayjs | null)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Stack>

            <FormControlLabel
              control={<Switch checked={useTimeRange} onChange={(e) => setUseTimeRange(e.target.checked)} />}
              label={t('page.meetingLink.unavailability.create.timeRange')}
            />

            {useTimeRange && (
              <Stack direction="row" spacing={2}>
                <TimePicker
                  label={t('page.meetingLink.unavailability.create.startTime')}
                  value={startTime}
                  ampm={false}
                  onChange={(value) => setStartTime(value as dayjs.Dayjs | null)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
                <TimePicker
                  label={t('page.meetingLink.unavailability.create.endTime')}
                  value={endTime}
                  ampm={false}
                  onChange={(value) => setEndTime(value as dayjs.Dayjs | null)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Stack>
            )}

            <TextField
              label={t('page.meetingLink.unavailability.create.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              fullWidth
            />

            <Button variant="outlined" onClick={handlePreview} disabled={!isValid || isPreviewing}>
              {t('page.meetingLink.unavailability.preview.button')}
            </Button>

            <Collapse in={previewed}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('page.meetingLink.unavailability.preview.title')}
              </Typography>
              {affectedOrders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('page.meetingLink.unavailability.preview.empty')}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                    {t('page.meetingLink.unavailability.preview.count', { count: affectedOrders.length })}
                  </Typography>
                  <List dense>
                    {affectedOrders.map((order) => (
                      <ListItem key={order._id}>
                        <ListItemText
                          primary={
                            order.createdBy
                              ? `${order.createdBy.userInfo?.name} ${order.createdBy.userInfo?.surname}`
                              : '-'
                          }
                          secondary={dayjs(order.startDate).format('DD/MM/YYYY HH:mm')}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('page.meetingLink.unavailability.create.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={handleSubmit} disabled={!isValid || isCreating}>
            {t('page.meetingLink.unavailability.create.submit')}
          </Button>
        </DialogActions>
        </LocalizationProvider>
      </Dialog>
    </>
  )
}
